import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { useState } from "react";
import Register from "./pages/Register";
import NotFound from "./pages/NotFound";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminUsers from "./pages/admin/AdminUsers";
import AdminAccessLog from "./pages/admin/AdminAccessLog";
import AdminRegistry from "./pages/admin/AdminRegistry";
import AdminCertificates from "./pages/admin/AdminCertificates";
import AdminCommercialization from "./pages/admin/AdminCommercialization";
import AdminReports from "./pages/admin/AdminReports";
import AdminTokenization from "./pages/admin/AdminTokenization";
import AdminProjects from "./pages/admin/AdminProjects";
import AdminProjectDetail from "./pages/admin/AdminProjectDetail";
import CategoryItems from "./pages/admin/CategoryItems";
import CompanyDetail from "./pages/admin/CompanyDetail";
import ClientDetail from "./pages/admin/ClientDetail";
import RegistryAccountConsult from "./pages/admin/RegistryAccountConsult";
import Profile from "./pages/Profile";
import UserPanel from "./pages/UserPanel";
import ProjectDetail from "./pages/ProjectDetail";
import ProjectUpdate from "./pages/ProjectUpdate";
import ProjectStatus from "./pages/ProjectStatus";
import ProjectRegion from "./pages/ProjectRegion";
import Dashboard from "./pages/Dashboard";
import MyProjects from "./pages/MyProjects";
import LandingPage from "./pages/LandingPage";
import AllProjects from "./pages/AllProjects";
import ProponenteRegistro from "./pages/ProponenteRegistro";
import ProponenteLogin from "./pages/ProponenteLogin";
import PregaoDashboard from "./pages/PregaoDashboard";
import PregaoLances from "./pages/PregaoLances";
import ProjetoOfertanteDetalhe from "./pages/ProjetoOfertanteDetalhe";
import PregaoDetalhe from "./pages/PregaoDetalhe";
import CadastroProponente from './pages/CadastroProponente';
import AreaProprietario from './pages/AreaProprietario';
import ProjetoDetalhe from './pages/ProjetoDetalhe';
import InvestirProjeto from './pages/InvestirProjeto';
import AdminPanel from "./pages/AdminPanel";
import CadastrarProjeto from "./pages/CadastrarProjeto";
import Certificates from "./pages/Certificates";
import Emission from "./pages/Emission";
import Traceability from "./pages/Traceability";
import Compliance from "./pages/Compliance";
import Retirement from "./pages/Retirement";
import Educational from "./pages/Educational";
import ClientLogin from "./pages/client/ClientLogin";
import ClientVerification from "./pages/client/ClientVerification";
import ClientDashboard from "./pages/client/ClientDashboard";
import ClientProfile from "./pages/client/ClientProfile";
import MeuProjetoDetalhe from './pages/MeuProjetoDetalhe';
import Planos from './pages/Planos';
import Relatorios from './pages/Relatorios';
// Partner Portal
import PartnerDashboard from './pages/partner/PartnerDashboard';
import PartnerOperations from './pages/partner/PartnerOperations';
import PartnerBeneficiaries from './pages/partner/PartnerBeneficiaries';
import PartnerBeneficiaryDetail from './pages/partner/PartnerBeneficiaryDetail';
import PartnerCertificates from './pages/partner/PartnerCertificates';
import PartnerLayout from './components/partner/PartnerLayout';
import PartnerRouteGuard from './components/partner/PartnerRouteGuard';

const queryClient = new QueryClient();

// Wrappers para componentes que precisam de props
const ProjectStatusWrapper = () => {
  return <ProjectStatus onBackToProjectType={() => window.history.back()} />;
};

const ProjectRegionWrapper = () => {
  return <ProjectRegion onBackToStatus={() => window.history.back()} />;
};

const App = () => {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loginError, setLoginError] = useState("");

  const handleLogin = (e) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setLoginError("Por favor, preencha todos os campos.");
      return;
    }
    
    // Verificação das credenciais específicas de administrador
    if (email === "admin@banclima.org.br" && password === "Admin123") {
      // Redirecionar para a página local que incorpora o painel administrativo
      setShowLoginModal(false);
      setEmail("");
      setPassword("");
      setLoginError("");
      
      // Navegar para a página AdminPanel em vez de abrir uma nova aba
      window.location.href = "/admin-panel";
    } else {
      setLoginError("Email ou senha incorretos. Acesso não autorizado.");
    }
  };

  return (
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          {/* Modal de Login */}
          {showLoginModal && (
            <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-lg shadow-lg max-w-md w-full p-6 relative">
                <button
                  onClick={() => setShowLoginModal(false)}
                  className="absolute top-3 right-3 text-gray-400 hover:text-gray-600"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
                
                <h2 className="text-2xl font-bold mb-6 text-center text-gray-800">Acessar Plataforma</h2>
                
                <form onSubmit={handleLogin} className="space-y-4">
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                    <input
                      id="email"
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0e7cb4]"
                      placeholder="seu@email.com"
                      required
                    />
                  </div>
                  
                  <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">Senha</label>
                    <input
                      id="password"
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#0e7cb4]"
                      placeholder="********"
                      required
                    />
                  </div>
                  
                  {loginError && (
                    <div className="text-red-500 text-sm">{loginError}</div>
                  )}
                  
                  <div>
                    <button
                      type="submit"
                      className="w-full bg-[#0e7cb4] hover:bg-[#0a5c87] text-white py-2 px-4 rounded font-medium"
                    >
                      Entrar
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
          
          <Routes>
            {/* Definir LandingPage como rota principal */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/register" element={<Register />} />
            <Route path="/admin-panel" element={<AdminPanel />} />
            <Route path="/registrar-projeto" element={<CadastrarProjeto />} />
            <Route path="/cadastrar-projeto" element={<CadastrarProjeto />} />
            <Route path="/profile" element={<Profile />} />
            <Route path="/dashboard" element={<Dashboard />} />
            <Route path="/panel" element={<UserPanel />} />
            <Route path="/project/:id" element={<ProjectDetail />} />
            <Route path="/project/update/:id" element={<ProjectUpdate />} />
            <Route path="/project/status" element={<ProjectStatusWrapper />} />
            <Route path="/project/region" element={<ProjectRegionWrapper />} />
            <Route path="/my-projects" element={<MyProjects />} />
            <Route path="/all-projects" element={<AllProjects />} />
            <Route path="/project/:id" element={<ProjectDetail />} />
            <Route path="/proponente-registro" element={<ProponenteRegistro />} />
            <Route path="/proponente-login" element={<ProponenteLogin />} />
            <Route path="/pregao-dashboard" element={<PregaoDashboard />} />
            <Route path="/pregao-lances/:id" element={<PregaoLances />} />
            <Route path="/projeto-ofertante/:id" element={<ProjetoOfertanteDetalhe />} />
            <Route path="/pregao-detalhe/:id" element={<PregaoDetalhe />} />
            <Route path="/cadastro-proponente" element={<CadastroProponente />} />
            <Route path="/area-proprietario" element={<AreaProprietario />} />
            
            {/* Nova rota para detalhes de projetos */}
            <Route path="/projeto/:id" element={<ProjetoDetalhe />} />
            <Route path="/investir/:id" element={<InvestirProjeto />} />
            
            {/* Novas rotas para as abas do menu horizontal */}
            <Route path="/moeda-social" element={<UserPanel />} />
            <Route path="/emissao" element={<Emission />} />
            <Route path="/rastreabilidade" element={<Traceability />} />
            <Route path="/certificados" element={<Certificates />} />
            <Route path="/compra-venda" element={<UserPanel />} />
            <Route path="/conformidade" element={<Compliance />} />
            <Route path="/aposentadoria" element={<Retirement />} />
            <Route path="/espaco-educativo" element={<UserPanel />} />
            
            {/* Rotas antigas, manter para compatibilidade */}
            <Route path="/carbon-currency" element={<UserPanel />} />
            <Route path="/emission" element={<Emission />} />
            <Route path="/traceability" element={<Traceability />} />
            <Route path="/certificates" element={<Certificates />} />
            <Route path="/marketplace" element={<UserPanel />} />
            <Route path="/compliance" element={<Compliance />} />
            <Route path="/retirement" element={<Retirement />} />
            
            <Route path="/admin" element={<AdminDashboard />} />
            <Route path="/admin/users" element={<AdminUsers />} />
            <Route path="/admin/access-log" element={<AdminAccessLog />} />
            <Route path="/admin/registry" element={<AdminRegistry />} />
            <Route path="/admin/certificates" element={<AdminCertificates />} />
            <Route path="/admin/tokenization" element={<AdminTokenization />} />
            <Route path="/admin/commercialization" element={<AdminCommercialization />} />
            <Route path="/admin/reports" element={<AdminReports />} />
            <Route path="/admin/projects" element={<AdminProjects />} />
            <Route path="/admin/projects/:id" element={<AdminProjectDetail />} />
            <Route path="/admin/projects/:categoryId/:subcategoryId?" element={<CategoryItems />} />
            <Route path="/admin/companies/:companyId" element={<CompanyDetail />} />
            <Route path="/admin/clients/:clientId" element={<ClientDetail />} />
            <Route path="/admin/registry-account-consult" element={<RegistryAccountConsult />} />
            <Route path="/educational" element={<Educational />} />
            <Route path="/meu-projeto/10" element={<MeuProjetoDetalhe />} />
            <Route path="/planos" element={<Planos />} />
            <Route path="/relatorios" element={<Relatorios />} />
            
            {/* Client Portal Routes */}
            <Route path="/client/login" element={<ClientLogin />} />
            <Route path="/client/verification" element={<ClientVerification />} />
            <Route path="/client/dashboard" element={<ClientDashboard />} />
            <Route path="/client/profile" element={<ClientProfile />} />
            
            {/* Partner Portal Routes */}
            <Route
              path="/partner/dashboard"
              element={
                <PartnerRouteGuard>
                  <PartnerLayout>
                    <PartnerDashboard />
                  </PartnerLayout>
                </PartnerRouteGuard>
              }
            />
            <Route
              path="/partner/operations"
              element={
                <PartnerRouteGuard>
                  <PartnerLayout>
                    <PartnerOperations />
                  </PartnerLayout>
                </PartnerRouteGuard>
              }
            />
            <Route
              path="/partner/beneficiaries"
              element={
                <PartnerRouteGuard>
                  <PartnerLayout>
                    <PartnerBeneficiaries />
                  </PartnerLayout>
                </PartnerRouteGuard>
              }
            />
            <Route
              path="/partner/beneficiaries/:beneficiaryId"
              element={
                <PartnerRouteGuard>
                  <PartnerLayout>
                    <PartnerBeneficiaryDetail />
                  </PartnerLayout>
                </PartnerRouteGuard>
              }
            />
            <Route
              path="/partner/certificates"
              element={
                <PartnerRouteGuard>
                  <PartnerLayout>
                    <PartnerCertificates />
                  </PartnerLayout>
                </PartnerRouteGuard>
              }
            />
            
            {/* ADD ALL CUSTOM ROUTES ABOVE THE CATCH-ALL "*" ROUTE */}
            <Route path="*" element={<NotFound />} />
          </Routes>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  );
};

export default App;
